import type { EnrichProposal } from "./enrichImport.js";
import {
  CONDITION_LABELS,
  MAX_FIELDS_PER_ITEM,
  type FieldInput,
  type Item,
  type ItemCondition,
  type ItemPatch,
} from "./types.js";
import {
  centsFromInput,
  clampName,
  clampText,
  clampTitle,
  normaliseTags,
  priceFromCents,
} from "./validate.js";

/**
 * Stage 3, the middle: a proposal set against what the item says now, one row
 * per thing that would change, and the patch that results from the rows
 * somebody accepted.
 *
 * **Nothing is saved from here.** `reviewRows()` and `applyReview()` are pure;
 * the screen decides when a patch is sent, and it is sent only on the person's
 * say-so. Rows exist only where the proposal *differs* from the item, so the
 * screen never asks about something that would not change.
 *
 * The defaults encode one bet: filling a blank is cheap to accept and
 * overwriting something a person wrote is not. A row whose current value is
 * empty starts on Accept; a row that would replace a value starts on Ignore.
 * Either way the person has to press Save.
 */

export type ReviewKind = "title" | "description" | "condition" | "category" | "price" | "tags" | "field";

export type ReviewAction = "accept" | "edit" | "ignore";

export interface ReviewRow {
  /** Stable within one item, so a screen can key a list and keep edits across renders. */
  key: string;
  kind: ReviewKind;
  /** What the row is called on screen: "Title", "Condition", or the field's own label. */
  label: string;
  /** The item's value as a person reads it. Empty means there is nothing there yet. */
  current: string;
  /** What the model said, as text; editable. For tags, only the ones the item does not have. */
  proposed: string;
  /** Where the row starts. */
  action: ReviewAction;
}

/** What a screen keeps per row: where it stands and, if edited, what it now says. */
export interface ReviewChoice {
  action: ReviewAction;
  value: string;
}

const same = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

const start = (current: string): ReviewAction => (current.trim() ? "ignore" : "accept");

export function reviewRows(
  item: Item,
  proposal: EnrichProposal,
  categories: readonly { id: string; name: string }[],
): ReviewRow[] {
  const rows: ReviewRow[] = [];

  const push = (
    kind: ReviewKind,
    key: string,
    label: string,
    current: string,
    proposed: string,
    action: ReviewAction = start(current),
  ) => {
    rows.push({ key, kind, label, current, proposed, action });
  };

  if (proposal.title !== undefined && proposal.title !== item.title.trim()) {
    push("title", "title", "Title", item.title, proposal.title);
  }
  if (proposal.description !== undefined && proposal.description !== item.description.trim()) {
    push("description", "description", "Description", item.description, proposal.description);
  }
  if (proposal.condition !== undefined && proposal.condition !== item.condition) {
    push(
      "condition",
      "condition",
      "Condition",
      item.condition ? CONDITION_LABELS[item.condition] : "",
      proposal.condition,
    );
  }
  if (proposal.category !== undefined) {
    const now = categories.find((category) => category.id === item.categoryId)?.name ?? "";
    if (!same(now, proposal.category)) push("category", "category", "Category", now, proposal.category);
  }
  if (proposal.priceCents !== undefined && proposal.priceCents !== item.priceCents) {
    push("price", "price", "Price", priceFromCents(item.priceCents), priceFromCents(proposal.priceCents));
  }
  if (proposal.tags !== undefined) {
    const have = new Set(item.tags.map((tag) => tag.toLowerCase()));
    const added = proposal.tags.filter((tag) => !have.has(tag.toLowerCase()));
    // Adding a tag overwrites nothing, so it starts on Accept even when the item has tags.
    if (added.length > 0) {
      push("tags", "tags", "Tags to add", item.tags.join(", "), added.join(", "), "accept");
    }
  }

  for (const field of proposal.fields ?? []) {
    const existing = item.fields.find((candidate) => same(candidate.label, field.label));
    if (existing && same(existing.value, field.value)) continue;
    // Keyed by the label, lowercased: two spellings of one label are one field.
    push("field", `field:${field.label.toLowerCase()}`, field.label, existing?.value ?? "", field.value);
  }

  return rows;
}

export type AppliedReview =
  | {
      ok: true;
      /** Only what was accepted. Empty when nothing was, which means there is nothing to send. */
      patch: ItemPatch;
      /** A category the model named that does not exist yet; the caller makes it and sets `categoryId`. */
      newCategory: string | null;
      accepted: number;
    }
  | { ok: false; reason: string };

export function applyReview(
  item: Item,
  rows: readonly ReviewRow[],
  choices: Readonly<Record<string, ReviewChoice>>,
  categories: readonly { id: string; name: string }[],
): AppliedReview {
  const patch: ItemPatch = {};
  let newCategory: string | null = null;
  let accepted = 0;
  const fields: FieldInput[] = item.fields.map(({ label, value, type, group, visibility }) => ({
    label,
    value,
    type,
    group,
    visibility,
  }));
  let fieldsChanged = false;
  let addedTags: string[] = [];

  for (const row of rows) {
    const choice = choices[row.key] ?? { action: row.action, value: row.proposed };
    if (choice.action === "ignore") continue;
    const value = choice.value.trim();
    // Accepting a row that was then edited to nothing is not a request to wipe
    // the value: clearing something is what the item screen is for.
    if (!value) continue;

    switch (row.kind) {
      case "title":
        patch.title = clampTitle(value);
        break;
      case "description":
        patch.description = clampText(value, 4000);
        break;
      case "condition": {
        const condition = (Object.keys(CONDITION_LABELS) as ItemCondition[]).find(
          (key) => key !== "" && (key === value || same(CONDITION_LABELS[key], value)),
        );
        if (!condition) return { ok: false, reason: `"${value}" is not a condition.` };
        patch.condition = condition;
        break;
      }
      case "category": {
        const match = categories.find((category) => same(category.name, value));
        if (match) patch.categoryId = match.id;
        else newCategory = clampName(value);
        break;
      }
      case "price": {
        const cents = centsFromInput(value);
        if (cents === null)
          return { ok: false, reason: `"${value}" is not a price. Use a plain number, like 12.50.` };
        patch.priceCents = cents;
        break;
      }
      case "tags":
        addedTags = value.split(",");
        break;
      case "field": {
        const at = fields.findIndex((field) => same(field.label, row.label));
        const existing = at === -1 ? undefined : fields[at];
        if (existing) fields[at] = { ...existing, value: clampText(value, 1000) };
        else if (fields.length < MAX_FIELDS_PER_ITEM) {
          // New fields are public and untyped, which is what a field is by default.
          fields.push({
            label: row.label,
            value: clampText(value, 1000),
            type: "text",
            group: "",
            visibility: "public",
          });
        } else {
          return {
            ok: false,
            reason: `That item already has ${MAX_FIELDS_PER_ITEM} fields, the most it can hold.`,
          };
        }
        fieldsChanged = true;
        break;
      }
    }
    accepted++;
  }

  if (fieldsChanged) patch.fields = fields;
  if (addedTags.length > 0) patch.tags = normaliseTags([...item.tags, ...addedTags]);

  return { ok: true, patch, newCategory, accepted };
}
